import React, { useEffect, useState } from "react";
import { ROLE } from "../../../../utils/constants/profiles";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import CardContainer from "./Card/CardContainer";
import CardLineChart from "./Chart/CardLineChart";
import CardBarChart from "./Chart/CardBarChart";
import CardPieChart from "./Chart/CardPieChart";
import { useSelector } from "react-redux";
import {
  useGetCardStatsQuery,
  useGetCategoryStatsQuery,
  useGetMonthWeekStatsQuery,
  useLazyGetDownloadStatsQuery,
} from "../../../api/admin/dashboardApi";
import {
  useGetUserCategoryStatsQuery,
  useGetUserMonthWeekStatsQuery,
  useGetUserStatsQuery,
} from "../../../api/user/userDashboardApi";
import CustomFactoryIcon from "../../../../app/assets/custom-icons/CustomFactoryIcon";
import CustomManageUsersIcon from "../../../../app/assets/custom-icons/CustomManageUsersIcon";
import CustomOperatorIcon from "../../../../app/assets/custom-icons/CustomOperatorIcon";
import CustomStudioManagementIcon from "../../../../app/assets/custom-icons/CustomStudioManagementIcon";
import { t } from "i18next";
import { hexToRgba } from "../../../../utils/functions/hexToRgba";
import CardCounter from "./Card/CardCounter";
import { toast } from "react-toastify";

const DocumentView = ({ filter, tab }) => {
  const role = useSelector((state) => state.app.role);
  const appConfig = useSelector((state) => state.app.appConfig);
  const colorPrimary = useSelector((state) => state.app.primaryColor);
  const colorSecondary = useSelector((state) => state.app.secondaryColor);
  const CardCount = ROLE.ADMIN === role ? 3 : ROLE.OPERATOR === role ? 1 : 2;
  const rgbaColor = hexToRgba(colorPrimary, 0.3);
  const [counter, setCounter] = useState({});
  const [card, setCard] = useState(
    Array(
      appConfig?.modules?.company?.general ? CardCount + 1 : CardCount
    ).fill({})
  );
  const [month, setMonth] = useState([]);
  const [week, setWeek] = useState([]);
  const [category, setCategory] = useState([]);

  // API ADMIN
  const { data: dataCard, isLoading: isLoadingCard } = useGetCardStatsQuery(
    filter,
    { refetchOnMountOrArgChange: true, skip: ROLE.USER === role || !tab }
  );
  const { data: dataChart, isLoading: isLoadingChart } =
    useGetMonthWeekStatsQuery(filter, {
      refetchOnMountOrArgChange: true,
      skip: ROLE.USER === role || !tab,
    });

  const { data: dataCategory, isLoading: isLoadingCategory } =
    useGetCategoryStatsQuery(filter, {
      refetchOnMountOrArgChange: true,
      skip: ROLE.USER === role || !tab,
    });

  // API USER
  const { data: dataUserCard, isLoading: isLoadingUserCard } =
    useGetUserStatsQuery(
      { refetchOnMountOrArgChange: true },
      { skip: ROLE.USER !== role || !tab }
    );
  const { data: dataUserChart, isLoading: isLoadingUserChart } =
    useGetUserMonthWeekStatsQuery(
      { refetchOnMountOrArgChange: true },
      { skip: ROLE.USER !== role || !tab }
    );

  const { data: dataUserCategory, isLoading: isLoadingUserCategory } =
    useGetUserCategoryStatsQuery(
      { refetchOnMountOrArgChange: true },
      { skip: ROLE.USER !== role || !tab }
    );

  const [downloadExcel] = useLazyGetDownloadStatsQuery();

  useEffect(() => {
    const processCountSummary = (data) => {
      setCounter({
        Tot: data?.Data?.totalDocuments,
        Validated: data?.Data?.validatedDocuments,
        NotValidated: data?.Data?.pendingDocuments,
        Reject: data?.Data?.rejectedDocuments,
      });
    };
    if (dataCard && dataCard?.Data) {
      setCard(
        [
          {
            id: 1,
            label: t("Users"),
            value: dataCard?.Data?.totalUsers,
            add: dataCard?.Data?.recentUsers,
            icon: <CustomManageUsersIcon className="w-20 h-20" />,
            show: true,
          },
          {
            id: 2,
            label: t("Operators"),
            value: dataCard?.Data?.totalOperators,
            add: dataCard?.Data?.recentOperators,
            icon: <CustomOperatorIcon className="w-20 h-20" />,
            show: ROLE.OPERATOR !== role,
          },
          {
            id: 3,
            label: t("Studios"),
            value: dataCard?.Data?.totalStudios,
            add: dataCard?.Data?.recentStudios,
            icon: <CustomStudioManagementIcon className="w-20 h-20" />,
            show: ROLE.ADMIN === role,
          },
          {
            id: 4,
            label: t("Companies"),
            value: dataCard?.Data?.totalCompanies,
            add: dataCard?.Data?.recentCompanies,
            icon: <CustomFactoryIcon className="w-20 h-20" />,
            show: appConfig.modules.company.general,
          },
        ].filter((el) => el.show)
      );

      processCountSummary(dataCard);
    }

    if (dataUserCard && dataUserCard?.Data) processCountSummary(dataUserCard);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCard, dataUserCard]);

  useEffect(() => {
    const processMonthlySummary = (data) => {
      const datiMensili = data?.Data?.monthlySummary.map((el) => ({
        month: el.month,
        uploaded: el.uploaded,
        validated: el.validated,
        rejected: el.rejected,
        pending: el.validating,
        year: el.year,
      }));
      setMonth(datiMensili);
    };

    const processWeeklySummary = (data) => {
      const datiSettimanali = data?.Data?.weeklySummary
        .map((el) => ({
          week: el.week,
          year: el.year,
          dailyDetails: el?.dailyDetails.map((el) => ({
            day:
              format(new Date(el.date), "EEEE", {
                locale: it,
              })
                .charAt(0)
                .toUpperCase() +
              format(new Date(el.date), "EEEE", {
                locale: it,
              }).slice(1),
            uploaded: el.uploaded,
            validated: el.validated,
            rejected: el.rejected,
            pending: el.validating,
          })),
        }))
        .pop(); // prendo sempre ultima week

      setWeek(datiSettimanali);
    };

    if (dataChart && dataChart?.Data) {
      processMonthlySummary(dataChart);
      processWeeklySummary(dataChart);
    }
    if (dataUserChart && dataUserChart?.Data) {
      processMonthlySummary(dataUserChart);
      processWeeklySummary(dataUserChart);
    }
  }, [dataChart, dataUserChart]);

  useEffect(() => {
    const processCategorySummary = (data) => {
      setCategory([
        {
          id: 0,
          value: data?.Data[2]?.documentCount,
          label: "Redditi",
          color: colorPrimary,
        },
        {
          id: 1,
          value: data?.Data[1]?.documentCount,
          label: "Detrazioni e Deduzioni",
          color: colorSecondary,
        },
        {
          id: 2,
          value: data?.Data[0]?.documentCount,
          label: "Altri Documenti",
          color: rgbaColor,
        },
      ]);
    };

    if (dataCategory && dataCategory?.Data)
      processCategorySummary(dataCategory);

    if (dataUserCategory && dataUserCategory?.Data)
      processCategorySummary(dataUserCategory);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCategory, dataUserCategory]);

  const excelRequest = (query) =>
    new Promise((resolve, reject) => {
      downloadExcel(query)
        .then((res) => {
          if (!res.isSuccess) throw new Error("Niente da fare");
          resolve(window.location.assign(window.URL.createObjectURL(res.data)));
        })
        .catch(() => reject());
    });

  const handleDownloadExcel = (query) => {
    toast.promise(
      excelRequest(query),
      {
        pending: {
          render: t("HandleDownload"),
          type: "info",
        },
        success: t("FileReady"),
        error: t("ReqErr"),
      },
      { autoClose: 1500 }
    );
  };

  return (
    <div className="py-8">
      {ROLE.USER !== role ? (
        <>
          <CardContainer
            card={card}
            counter={counter}
            isLoading={isLoadingCard}
          />
          <div className="py-8 m-4">
            <CardLineChart
              dataMonth={month}
              downloadExcel={handleDownloadExcel}
            />
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 m-4 py-8">
            <CardBarChart
              dataWeek={week?.dailyDetails}
              downloadExcel={handleDownloadExcel}
            />
            <CardPieChart
              dataCategory={category}
              downloadExcel={handleDownloadExcel}
            />
          </div>
        </>
      ) : (
        <>
          <CardCounter counter={counter} isLoading={isLoadingUserCard} />
          <div className="py-8 m-4">
            <CardLineChart dataMonth={month} />
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 m-4 py-8">
            <CardBarChart dataWeek={week?.dailyDetails} />
            <CardPieChart dataCategory={category} />
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentView;
